import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import LicenseeFilter from '../components/common/LicenseeFilter';
import {
  Box,
  Typography,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Autocomplete,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  TextField,
  Grid,
} from '@mui/material';
import {
  SortableHeaderCell,
  makeSortPaginationHandlers,
  AdminTablePagination,
  StatusSnackbars,
} from './_shared/adminListShared_tableUtils';
import {
  Add,
  Edit,
  Delete,
  ToggleOn,
  ToggleOff,
  Search,
  Login as LoginIcon,
  LockReset,
  FileDownload,
  CheckBoxOutlineBlank,
  CheckBox as CheckBoxIcon,
} from '@mui/icons-material';
import { User, AccountType, AccessTypeAnalytics } from '../types/auth';
import { Company, SubCompany, Branch, SubCustomer } from '../types/company';
import { userApi } from '../services/api/userApi';
import { companyApi } from '../services/api/companyApi';
import { apiClient } from '../services/api/authApi';
import { useAuthStore } from '../store/authStore';
import * as XLSX from 'xlsx';
import {
  CreateUserData,
  validateRbacFields,
  validateRequiredUserFields,
  makeHandleInputChange,
  makeUserFormChangeHandlers,
  applyUserStatusToggle,
} from '../components/users/userFormShared';
import UserFormDialog from '../components/users/UserFormDialog';

const roleOptions: AccountType[] = [
  // SubCustomerAdmin, BranchAdmin and SubCompanyAdmin are hidden from the
  // role filter for now (not needed yet).
  AccountType.User,
  AccountType.LicenseeAdmin,
  AccountType.CarbonCareAdmin,
  AccountType.MasterAdmin,
];

// Pure per-row permission computation, extracted from the table body to keep
// the render callback's cognitive complexity low.
const computeUserRowPermissions = (u: User, currentUser: User | null) => {
  const isActive = (u as any).isActive ?? (u as any).is_active ?? false;
  const targetAccountType = u.accountType ?? (u as any).account_type;
  const isTargetMasterAdmin = targetAccountType === AccountType.MasterAdmin;
  const isSelf = u.id === currentUser?.id;
  // CarbonCare admin: edit only users belonging to its own licensee/company
  // (matching companyId); view-only for all other users, and no
  // destructive actions.
  const isCarbonCareAdmin = currentUser?.accountType === AccountType.CarbonCareAdmin;
  const isOwnCompanyUser = u.companyId === currentUser?.companyId;
  const canEdit = isCarbonCareAdmin
    ? (isOwnCompanyUser && !isTargetMasterAdmin)
    : !(currentUser?.accountType === AccountType.MasterAdmin && isTargetMasterAdmin && !isSelf);
  const canLoginAs =
    currentUser?.accountType === AccountType.MasterAdmin &&
    u.id !== currentUser?.id &&
    u.accountType !== AccountType.MasterAdmin;
  const canReset2FA =
    !isCarbonCareAdmin &&
    !!currentUser?.accountType &&
    currentUser.accountType >= AccountType.CarbonCareAdmin &&
    (u as any).totpEnabled &&
    u.id !== currentUser?.id &&
    u.accountType < currentUser.accountType;
  const canToggleStatus = !isCarbonCareAdmin && !(currentUser?.accountType === AccountType.MasterAdmin && isTargetMasterAdmin);
  const canDelete = !isCarbonCareAdmin && !(currentUser?.accountType === AccountType.MasterAdmin && isTargetMasterAdmin);
  return { isActive, canEdit, canLoginAs, canReset2FA, canToggleStatus, canDelete };
};

const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuthStore();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState<AccountType[]>([]);
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateUserData>({
    firstName: '',
    lastName: '',
    email: '',
    accountType: AccountType.SubCustomerAdmin,
    companyId: 0,
    branch: '',
    subCompany: '',
    mail: '',
    accessTypeAnalytics: AccessTypeAnalytics.Passive,
    dataStorageKey: '',
    passKey: '',
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // State for hierarchical organization selection
  const [subCompanies, setSubCompanies] = useState<SubCompany[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [subCustomers, setSubCustomers] = useState<SubCustomer[]>([]);
  const [selectedSubCompanyId, setSelectedSubCompanyId] = useState<number | null>(null);
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [selectedSubCustomerId, setSelectedSubCustomerId] = useState<number | null>(null);
  const [loadingOrgData, setLoadingOrgData] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, accountTypeFilter]);

  // When a role filter is active, default the listing to last-name ascending.
  useEffect(() => {
    if (accountTypeFilter.length > 0) {
      setSortBy('name');
      setSortDir('asc');
    }
  }, [accountTypeFilter]);

  // Fetch users from backend whenever pagination, filters, or sort change
  useEffect(() => {
    loadUsers();
  }, [page, rowsPerPage, debouncedSearch, accountTypeFilter, sortBy, sortDir]);

  const loadOrgData = async (companyId: number) => {
    try {
      setLoadingOrgData(true);
      const [subCompaniesData, branchesData, subCustomersData] = await Promise.all([
        companyApi.getSubCompanies(companyId),
        companyApi.getBranches(companyId),
        companyApi.getSubCustomers(companyId),
      ]);
      setSubCompanies(subCompaniesData);
      setBranches(branchesData);
      setSubCustomers(subCustomersData);
    } catch (error) {
      console.error('Failed to load organizational data:', error);
    } finally {
      setLoadingOrgData(false);
    }
  };

  // Load organizational data when dialog opens and company is selected
  useEffect(() => {
    if (openDialog && formData.companyId > 0) {
      loadOrgData(formData.companyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, formData.companyId]);

  // Resolve sub-company and branch IDs after org data loads for editing
  useEffect(() => {
    if (!editingUser || subCompanies.length === 0) return;

    if (editingUser.subCompany) {
      const sc = subCompanies.find(s => s.code === editingUser.subCompany || s.name === editingUser.subCompany);
      if (sc) {
        setSelectedSubCompanyId(sc.id);
      }
    }

    if (editingUser.branch && editingUser.accountType === AccountType.BranchAdmin && branches.length > 0) {
      const br = branches.find(b => b.code === editingUser.branch || b.name === editingUser.branch);
      if (br) {
        setSelectedBranchId(br.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCompanies, branches, editingUser]);

  // Reset branch selection when sub-company changes (only for user-driven changes, not edit init)
  const prevSubCompanyIdRef = React.useRef<number | null>(null);
  useEffect(() => {
    // Skip if this is the initial resolution from editingUser
    if (prevSubCompanyIdRef.current !== null && selectedSubCompanyId !== null &&
        prevSubCompanyIdRef.current !== selectedSubCompanyId) {
      setSelectedBranchId(null);
      handleInputChange('branch', '');
    }
    prevSubCompanyIdRef.current = selectedSubCompanyId;
  }, [selectedSubCompanyId]);

  // Reset organizational selections when account type changes
  useEffect(() => {
    if (formData.accountType !== AccountType.SubCompanyAdmin &&
      formData.accountType !== AccountType.BranchAdmin) {
      setSelectedSubCompanyId(null);
      setSelectedBranchId(null);
    }
  }, [formData.accountType]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userApi.getAll({
        page: page + 1, // 0-indexed UI, 1-indexed API
        limit: rowsPerPage,
        sortBy,
        sortOrder: sortDir,
        ...(accountTypeFilter.length > 0 && { accountTypes: accountTypeFilter }),
        ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
      });
      setUsers(response.data);
      setTotalUsers(response.total);
    } catch (error: any) {
      console.error('Failed to load users:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const result = await companyApi.getAll({ limit: 1000, fields: 'id,name' }); // Only fetch id and name for user assignment
      setCompanies(result.data);
    } catch (error: any) {
      console.error('Failed to load companies:', error);
    }
  };

  const handleCreateClick = () => {
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      accountType: AccountType.User,
      companyId: currentUser?.companyId || 0,
      branch: '',
      subCompany: '',
      mail: '',
      accessTypeAnalytics: AccessTypeAnalytics.Passive,
      dataStorageKey: '',
      passKey: '',
    });
    setValidationErrors({});
    setSelectedSubCompanyId(null);
    setSelectedBranchId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      accountType: user.accountType,
      companyId: user.companyId,
      branch: user.branch || '',
      subCompany: user.subCompany || '',
      subCustomerId: user.subCustomerId || undefined,
      mail: user.mail || '',
      accessTypeAnalytics: user.accessTypeAnalytics || AccessTypeAnalytics.Passive,
      dataStorageKey: user.dataStorageKey || '',
      passKey: user.passKey || '',
    });

    // Clear selections; sub-company/branch will be resolved after org data loads
    setSelectedSubCompanyId(null);
    setSelectedBranchId(null);
    // subCustomerId is already a numeric ID, no resolution needed
    setSelectedSubCustomerId(user.subCustomerId || null);

    setValidationErrors({});
    setOpenDialog(true);
  };

  const handleInputChange = makeHandleInputChange(formData, setFormData, setValidationErrors, editingUser);

  const handleSubmit = async () => {
    try {
      // Final validation
      const errors: Record<string, string> = {};
      validateRequiredUserFields(formData, errors);
      if (formData.companyId === 0) errors.companyId = 'Licensee selection is required';

      // Validate RBAC role requirements
      validateRbacFields(formData, errors, 'Company Admin');

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }

      if (editingUser) {
        const updateData = { ...formData };

        const updatedUser = await userApi.update(editingUser.id, updateData);
        setUsers(users.map(u => u.id === editingUser.id ? updatedUser : u));
        setSuccess('User updated successfully');
      } else {
        const response = await userApi.create(formData);
        // Backend returns { user, emailSent }, extract just the user
        const newUser = response.user || response;
        setUsers([...users, newUser]);
        setSuccess('User created successfully');
      }
      setOpenDialog(false);
      setSearchTerm('');
      setDebouncedSearch('');
      setAccountTypeFilter([]);
      setPage(0);
    } catch (error: any) {
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to save user');
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      const result = await applyUserStatusToggle(user, users);
      setUsers(result.users);
      setSuccess(result.successMessage);
    } catch (error: any) {
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to update user status');
      console.error('Toggle user status error:', error);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (globalThis.confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}?`)) {
      try {
        await userApi.delete(user.id);
        setUsers(users.filter(u => u.id !== user.id));
        setSuccess('User deleted successfully');
      } catch (error: any) {
        setError(error.response?.data?.error || error.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const handleLoginAs = async (targetUser: User) => {
    if (globalThis.confirm(`Are you sure you want to login as ${targetUser.firstName} ${targetUser.lastName}?`)) {
      try {
        const response = await apiClient.post(`/auth/impersonate/${targetUser.id}`);
        if (response.data.success) {
          const authStore = useAuthStore.getState();
          authStore.setAuth(response.data.data.user, response.data.data.token);
          navigate('/settings', { replace: true });
        }
      } catch (error: any) {
        setError(error.response?.data?.error || error.response?.data?.message || 'Failed to impersonate user');
      }
    }
  };

  const handleReset2FA = async (targetUser: User) => {
    if (globalThis.confirm(`Are you sure you want to reset 2FA for ${targetUser.firstName} ${targetUser.lastName} (${targetUser.email})?\n\nThis will disable their two-factor authentication and they will need to set it up again.`)) {
      try {
        const result = await userApi.reset2FA(targetUser.id);
        setUsers(users.map(u => u.id === targetUser.id ? { ...u, totpEnabled: false } : u));
        setSuccess(result.message || `2FA has been reset for ${targetUser.email}`);
      } catch (error: any) {
        setError(error.response?.data?.error || 'Failed to reset 2FA');
      }
    }
  };

  const handleExport = async () => {
    try {
      const blob = await userApi.exportCsv({
        search: debouncedSearch || undefined,
        accountTypes: accountTypeFilter.length > 0 ? accountTypeFilter : undefined,
      });
      const csvText = await blob.text();
      const workbook = XLSX.read(csvText, { type: 'string' });
      const sheetName = workbook.SheetNames[0] || 'Users';
      if (!workbook.SheetNames.includes('Users')) {
        workbook.Sheets['Users'] = workbook.Sheets[sheetName];
        workbook.SheetNames = ['Users'];
      }
      XLSX.writeFile(workbook, `users_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to export users');
    }
  };

  const getAccountTypeLabel = (type: AccountType): string => {
    switch (type) {
      case AccountType.User:
        return 'User';
      case AccountType.LicenseeAdmin:
        return 'Licensee Admin';
      case AccountType.MasterAdmin:
        return 'Master Admin';
      case AccountType.CarbonCareAdmin:
        return 'Carboncare Admin';
      // case AccountType.SubCustomer:
      //   return 'Sub Customer';
      case AccountType.BranchAdmin:
        return 'Branch Admin';
      case AccountType.SubCompanyAdmin:
        return 'Sub Company Admin';
      case AccountType.SubCustomerAdmin:
        return 'SubCustomer Admin';
      default:
        return 'Unknown';
    }
  };

  const getAccountTypeColor = (type: AccountType) => {
    switch (type) {
      case AccountType.User:
        return 'default';
      case AccountType.LicenseeAdmin:
        return 'secondary';
      case AccountType.MasterAdmin:
        return 'error';
      case AccountType.CarbonCareAdmin:
        return 'error';
      // case AccountType.SubCustomer:
      //   return 'info';
      case AccountType.BranchAdmin:
        return 'warning';
      case AccountType.SubCompanyAdmin:
        return 'success';
      case AccountType.SubCustomerAdmin:
        return 'info';
      default:
        return 'default';
    }
  };

  const { handleChangePage, handleChangeRowsPerPage, handleSort } = makeSortPaginationHandlers(
    { page, rowsPerPage, sortBy, sortDir },
    setPage,
    setRowsPerPage,
    setSortBy,
    setSortDir,
  );
  const sortableHeader = (field: string, label: string) => (
    <SortableHeaderCell field={field} label={label} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
  );

  const show2FAColumn = !!(currentUser?.accountType && currentUser.accountType >= AccountType.CarbonCareAdmin);
  const showActionsColumn =
    !!(currentUser?.accountType && currentUser.accountType >= AccountType.SubCompanyAdmin);

  const getLicenseeName = (user: User) => {
    if (user.companyName) return user.companyName;
    const company = companies.find(c => c.id === user.companyId);
    return company?.name || 'Unknown';
  };

  const totalColumns = 5 + (show2FAColumn ? 1 : 0) + (showActionsColumn ? 1 : 0);

  return (
    <Box>
      <PageHeader
        description="Manage user accounts, roles, and permissions."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExport}
              size="large"
            >
              Export XLSX
            </Button>
            {currentUser?.accountType !== AccountType.CarbonCareAdmin && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateClick}
                size="large"
              >
                Add User
              </Button>
            )}
          </>
        }
      />

      {/* Search Bar */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          backgroundColor: 'grey.50',
          borderRadius: 2,
          px: 2,
          py: 1,
          border: '1px solid',
          borderColor: 'grey.200',
        }}>
          <TextField
            placeholder="Search users by name, email, position, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            InputProps={{
              startAdornment: <Search sx={{ mr: 1.5, color: 'primary.main' }} />,
            }}
            sx={{
              mb: 0,
              flex: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                borderRadius: 2,
                fontSize: '0.95rem',
                '& fieldset': {
                  borderColor: 'grey.300',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 2,
                },
              },
              '& .MuiInputBase-input': {
                py: 1.5,
              },
            }}
          />
          <Autocomplete
            multiple
            limitTags={1}
            options={roleOptions}
            disableCloseOnSelect
            getOptionLabel={(option) => getAccountTypeLabel(option)}
            value={accountTypeFilter}
            onChange={(_event, newValue) => {
              setAccountTypeFilter(newValue);
            }}
            renderTags={(value, getTagProps) => {
              if (value.length === 0) return null;
              if (value.length === 1) {
                return (
                  <Chip
                    label={getAccountTypeLabel(value[0])}
                    size="small"
                    color="primary"
                    variant="filled"
                    sx={{ maxWidth: 150 }}
                    {...getTagProps({ index: 0 })}
                  />
                );
              }
              return (
                <Chip
                  label={`${value.length} roles selected`}
                  size="small"
                  color="primary"
                  variant="filled"
                  onDelete={() => setAccountTypeFilter([])}
                />
              );
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {getAccountTypeLabel(option)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Role"
                placeholder={accountTypeFilter.length === 0 ? 'Filter by role...' : ''}
              />
            )}
            sx={{
              width: 250,
              mt: 1.5,
              '& .MuiAutocomplete-inputRoot': {
                flexWrap: 'nowrap',
              },
            }}
          />
          {(searchTerm || accountTypeFilter.length > 0) && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'primary.main',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
            }}>
              {totalUsers} users
            </Box>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortBy === 'name' ? sortDir : false}>
                {sortableHeader('name', 'Name')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'email' ? sortDir : false}>
                {sortableHeader('email', 'Email')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'accountType' ? sortDir : false}>
                {sortableHeader('accountType', 'Role')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'licensee' ? sortDir : false}>
                {sortableHeader('licensee', 'Licensee')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'status' ? sortDir : false}>
                {sortableHeader('status', 'Status')}
              </TableCell>
              {show2FAColumn && (
                <TableCell sortDirection={sortBy === 'totp' ? sortDir : false}>
                  {sortableHeader('totp', '2FA')}
                </TableCell>
              )}
              {showActionsColumn && <TableCell align="center">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              if (loading) return (
              Array.from({ length: 10 }, (_, i) => `user-skeleton-${i}`).map((rowKey, idx) => (
                <TableRow key={rowKey}>
                  <TableCell><Skeleton variant="text" width={`${50 + ((idx * 9) % 30)}%`} /></TableCell>
                  <TableCell><Skeleton variant="text" width={`${55 + ((idx * 7) % 25)}%`} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={90} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
                  <TableCell><Skeleton variant="text" width={120} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={64} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
                  {show2FAColumn && (
                    <TableCell><Skeleton variant="rounded" width={36} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
                  )}
                  {showActionsColumn && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <Skeleton variant="circular" width={28} height={28} />
                        <Skeleton variant="circular" width={28} height={28} />
                        <Skeleton variant="circular" width={28} height={28} />
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            );
              if (users.length === 0) return (
                  <TableRow>
                    <TableCell colSpan={totalColumns} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No users found</Typography>
                    </TableCell>
                  </TableRow>
                );
              return (
                  users.map((u) => {
                    const { isActive, canEdit, canLoginAs, canReset2FA, canToggleStatus, canDelete } =
                      computeUserRowPermissions(u, currentUser);

                    return (
                      <TableRow key={u.id} hover>
                        <TableCell> {u.lastName} {u.firstName}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={getAccountTypeLabel(u.accountType)}
                            color={getAccountTypeColor(u.accountType)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{getLicenseeName(u)}</TableCell>
                        <TableCell>
                          <Chip
                            label={isActive ? 'Active' : 'Inactive'}
                            color={isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        {show2FAColumn && (
                          <TableCell>
                            <Chip
                              label={(u as any).totpEnabled ? 'On' : 'Off'}
                              color={(u as any).totpEnabled ? 'primary' : 'default'}
                              size="small"
                              variant={(u as any).totpEnabled ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                        )}
                        {showActionsColumn && (
                          <TableCell align="center">
                            {canEdit && (
                              <Tooltip title="Edit user" arrow>
                                <IconButton size="small" onClick={() => handleEditClick(u)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canLoginAs && (
                              <Tooltip title="Login as this user" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleLoginAs(u)}
                                  sx={{ color: 'primary.main' }}
                                >
                                  <LoginIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canReset2FA && (
                              <Tooltip title="Reset 2FA" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleReset2FA(u)}
                                  sx={{ color: 'warning.main' }}
                                >
                                  <LockReset fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canToggleStatus && (
                              <Tooltip title={isActive ? 'Deactivate user' : 'Activate user'} arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleStatus({ ...u, isActive } as any)}
                                  sx={{ color: isActive ? 'success.main' : 'error.main' }}
                                >
                                  {isActive ? <ToggleOn fontSize="small" /> : <ToggleOff fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Delete user" arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteUser(u)}
                                    disabled={u.id === currentUser?.id}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                );
            })()}
          </TableBody>
        </Table>
        {!loading && (
          <AdminTablePagination
            count={totalUsers}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>

      {/* Create/Edit Dialog */}
      <UserFormDialog
        open={openDialog}
        editingUser={editingUser}
        formData={formData}
        validationErrors={validationErrors}
        subCompanies={subCompanies}
        branches={branches}
        subCustomers={subCustomers}
        selectedSubCompanyId={selectedSubCompanyId}
        selectedBranchId={selectedBranchId}
        selectedSubCustomerId={selectedSubCustomerId}
        loadingOrgData={loadingOrgData}
        onCancel={() => { setOpenDialog(false); setSearchTerm(''); }}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
        {...makeUserFormChangeHandlers(
          setSelectedSubCompanyId,
          setSelectedBranchId,
          setSelectedSubCustomerId,
          handleInputChange,
        )}
        subCompanyAdminHintText="Required for Company Admin role"
        companySectionSlot={
          <>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
              Company &amp; Role
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <LicenseeFilter
                  value={formData.companyId || ''}
                  onChange={(id) => handleInputChange('companyId', id || 0)}
                  required
                  label="Licensee"
                  error={!!validationErrors.companyId}
                  sx={{ width: '100%' }}
                />
                {validationErrors.companyId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5, display: 'block' }}>
                    {validationErrors.companyId}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.accountType}
                    onChange={(e) => handleInputChange('accountType', e.target.value)}
                    label="Role"
                    error={!!validationErrors.accountType}
                  >
                    {currentUser?.accountType! >= AccountType.User && (
                      <MenuItem key="user" value={AccountType.User}>User</MenuItem>
                    )}
                    {currentUser?.accountType! >= AccountType.LicenseeAdmin && (
                      <MenuItem key="licensee-admin" value={AccountType.LicenseeAdmin}>Licensee Admin</MenuItem>
                    )}
                    {currentUser?.accountType! >= AccountType.CarbonCareAdmin && (
                      <MenuItem key="carbon-care-admin" value={AccountType.CarbonCareAdmin}>CarbonCare Admin</MenuItem>
                    )}
                    {currentUser?.accountType === AccountType.MasterAdmin && (
                      <MenuItem key="master-admin" value={AccountType.MasterAdmin}>Master Admin</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </>
        }
      />

      {/* Success/Error Snackbars */}
      <StatusSnackbars
        success={success}
        error={error}
        onClearSuccess={() => setSuccess(null)}
        onClearError={() => setError(null)}
      />
    </Box>
  );
};

export default UsersPage;
